import { useQuery } from '@tanstack/react-query'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { can } from '@/shared/auth/permissions'
import { meQueryOptions, tenantQueryOptions } from '@/shared/auth/session'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/shared/ui/sheet'

import { CommandMenu } from './command-menu'
import { CurrentNavContext, findCurrentNavItem } from './current-nav'
import { HeaderBreadcrumb } from './header-breadcrumb'
import { HelpMenu } from './help-menu'
import { visibleNavItems, type NavItem } from './navigation'
import { NotificationsMenu } from './notifications-menu'
import { SidebarBrand } from './sidebar-brand'
import { SidebarNav } from './sidebar-nav'
import { SidebarUser } from './sidebar-user'

interface AppShellProps {
  navigation: NavItem[]
}

/**
 * Estrutura das telas autenticadas: menu lateral recolhível (Sheet no mobile)
 * com o usuário no rodapé, barra superior com busca, ajuda, notificações e
 * tema, e a área de conteúdo do módulo.
 */

const COLLAPSED_KEY = 'appx.sidebar.collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
  } catch {
    // Sem armazenamento (aba privada): o menu só não lembra o estado.
  }
}

export function AppShell({ navigation }: AppShellProps) {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const { data: admin } = useQuery(meQueryOptions)
  const { data: tenant } = useQuery(tenantQueryOptions)

  const items = visibleNavItems(navigation, (item) => can(admin, item.permission))
  const brand = tenant?.branding.title ?? tenant?.name ?? t('app.name')
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = findCurrentNavItem(items, pathname)

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      writeCollapsed(!value)
      return !value
    })
  }

  return (
    <div className="flex min-h-svh">
      {/* Reserva a largura do menu, que é fixo: inset-y-0 acompanha a janela real (vh/dvh erra em alguns desktops). */}
      <div
        aria-hidden
        className={cn('hidden shrink-0 transition-[width] md:block', collapsed ? 'w-16' : 'w-62')}
      />
      <aside
        data-collapsed={collapsed || undefined}
        className={cn(
          'fixed inset-y-0 left-0 z-20 hidden bg-sidebar text-sidebar-foreground transition-[width] md:flex md:flex-col',
          collapsed ? 'w-16' : 'w-62',
        )}
      >
        <SidebarBrand branding={tenant?.branding} name={brand} collapsed={collapsed} />
        <SidebarNav items={items} current={current} collapsed={collapsed} />
        {admin ? <SidebarUser admin={admin} collapsed={collapsed} /> : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-3 border-b bg-card/90 px-4 backdrop-blur supports-[backdrop-filter]:bg-card/75 md:px-7">
          <Button
            variant="ghost"
            size="icon"
            className="hidden text-muted-foreground md:inline-flex"
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            <PanelMenuIcon />
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              render={
                <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('nav.openMenu')} />
              }
            >
              <Menu />
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-sidebar p-0 text-sidebar-foreground">
              <SheetHeader className="sr-only">
                <SheetTitle>{brand}</SheetTitle>
              </SheetHeader>
              <SidebarBrand branding={tenant?.branding} name={brand} />
              <SidebarNav items={items} current={current} onNavigate={() => setMobileOpen(false)} />
              {admin ? <SidebarUser admin={admin} /> : null}
            </SheetContent>
          </Sheet>

          <span className="truncate font-display text-lg font-bold md:hidden">{brand}</span>

          <div aria-hidden className="hidden h-6 w-px bg-border md:block" />
          <HeaderBreadcrumb items={items} current={current} />

          <div className="ml-auto flex items-center gap-2">
            <CommandMenu items={items} />
            <div aria-hidden className="mx-1 hidden h-6 w-px bg-border sm:block" />
            <HelpMenu />
            {admin ? <NotificationsMenu admin={admin} /> : null}
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 p-4 md:p-7">
          <CurrentNavContext.Provider value={current}>
            {/* A chave reinicia a animação de entrada a cada troca de página. */}
            <div key={pathname} className="animate-enter">
              <Outlet />
            </div>
          </CurrentNavContext.Provider>
        </main>
      </div>
    </div>
  )
}

/** Ícone de recolher do canvas: três linhas com a do meio mais curta. */
function PanelMenuIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M4 7h16M4 12h10M4 17h16" />
    </svg>
  )
}
